import { Controller } from "stimulus"

const IGNORED_FORM_KEYS = ["authenticity_token", "utf8", "commit"]

let historyInstalled = false

function installHistoryListener() {
  if (historyInstalled) return
  historyInstalled = true

  window.addEventListener("popstate", function(event) {
    if (!event.state || !event.state.liveFilter) return

    event.stopImmediatePropagation()

    const root = document.querySelector("[data-controller~='live-filter']")
    const controller = root && root.liveFilterController
    if (controller) {
      controller.visit(window.location.href, { historyMode: "none" })
      return
    }

    window.location.reload()
  }, true)
}

export default class extends Controller {
  connect() {
    this.requestId = 0
    this.element.liveFilterController = this
    installHistoryListener()
    this.disableTurbolinksCache()
    this.onClick = this.onClick.bind(this)
    this.onSubmit = this.onSubmit.bind(this)
    this.element.addEventListener("click", this.onClick)
    this.element.addEventListener("submit", this.onSubmit)
  }

  disconnect() {
    if (this.element.liveFilterController === this) {
      this.element.liveFilterController = null
    }
    this.element.removeEventListener("click", this.onClick)
    this.element.removeEventListener("submit", this.onSubmit)
    this.stopBusy()
    if (this.abortController) this.abortController.abort()
  }

  onClick(event) {
    const paginationLink = event.target.closest("[data-live-filter-pagination] a[href]")
    const filterLink = event.target.closest("a[data-live-filter-link]")
    const link = paginationLink || filterLink
    if (!link || !this.element.contains(link)) return
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return
    if (link.target === "_blank") return

    event.preventDefault()
    event.stopPropagation()
    this.visit(link.href)
  }

  onSubmit(event) {
    const form = event.target.closest("form[data-live-filter-form]")
    if (!form || !this.element.contains(form)) return

    event.preventDefault()
    event.stopPropagation()
    this.visit(this.urlFromForm(form))
  }

  visit(url, options) {
    const historyMode = options && options.historyMode ? options.historyMode : "push"
    const nextUrl = new URL(url, window.location.origin)
    if (nextUrl.origin !== window.location.origin) {
      window.location.assign(nextUrl.href)
      return
    }

    const requestId = ++this.requestId
    if (this.abortController) this.abortController.abort()
    const abortController = new AbortController()
    this.abortController = abortController
    this.startBusy()

    fetch(nextUrl.href, {
      method: "GET",
      credentials: "same-origin",
      cache: "no-store",
      signal: abortController.signal,
      headers: {
        Accept: "text/html",
        "X-Requested-With": "XMLHttpRequest"
      }
    }).then((response) => {
      if (!response.ok) throw new Error("filter request failed")
      return response.text()
    }).then((html) => {
      if (requestId !== this.requestId) return

      const doc = new DOMParser().parseFromString(html, "text/html")
      if (!doc.querySelector("[data-controller~='live-filter']")) {
        window.location.assign(nextUrl.href)
        return
      }

      this.replaceRegions(doc)
      this.writeHistory(nextUrl.href, historyMode)
      if (doc.title) document.title = doc.title
    }).catch((error) => {
      if (error && error.name === "AbortError") return
      if (requestId !== this.requestId) return
      window.location.assign(nextUrl.href)
    }).finally(() => {
      if (requestId === this.requestId) this.stopBusy()
    })
  }

  replaceRegions(doc) {
    const panel = this.element.querySelector("[data-live-filter-region='panel']")
    const panelState = this.capturePanelState(panel)

    ;["toolbar", "results", "panel"].forEach((name) => {
      const current = this.element.querySelector("[data-live-filter-region='" + name + "']")
      const next = doc.querySelector("[data-live-filter-region='" + name + "']")
      if (!current || !next) return
      current.innerHTML = next.innerHTML
    })

    this.restorePanelState(panel, panelState)
    this.refreshWidgets(this.element.querySelector("[data-live-filter-region='results']"))
  }

  capturePanelState(panel) {
    if (!panel) return null

    return {
      scrollTop: panel.scrollTop,
      closed: Array.prototype.map.call(panel.querySelectorAll("details"), function(details) {
        return !details.open
      })
    }
  }

  restorePanelState(panel, state) {
    if (!panel || !state) return

    panel.querySelectorAll("details").forEach(function(details, index) {
      if (state.closed[index]) details.removeAttribute("open")
    })
    panel.scrollTop = state.scrollTop
  }

  refreshWidgets(region) {
    if (!region || !window.jQuery) return

    window.jQuery(region).find("[data-toggle='tooltip']").tooltip()
    window.jQuery(region).find("img.lazyload").lazyload()
    if (window.loadDrillAnimations) window.loadDrillAnimations(region)

    if (!window.sorttable) return
    region.querySelectorAll("table.sortable").forEach(function(table) {
      window.sorttable.makeSortable(table)
    })
  }

  writeHistory(url, historyMode) {
    if (historyMode === "none") return
    if (historyMode !== "replace" && url === window.location.href) return

    const state = { liveFilter: true }
    if (historyMode === "replace") {
      history.replaceState(state, "", url)
    } else {
      history.pushState(state, "", url)
    }
  }

  urlFromForm(form) {
    const action = new URL(form.getAttribute("action") || window.location.href, window.location.origin)
    const params = new URLSearchParams()
    const data = new FormData(form)

    data.forEach(function(value, key) {
      if (IGNORED_FORM_KEYS.indexOf(key) !== -1) return
      const text = String(value).trim()
      if (text === "") return
      if (key.slice(-2) === "[]") {
        params.append(key, text)
      } else {
        params.set(key, text)
      }
    })

    action.search = params.toString()
    return action.href
  }

  startBusy() {
    const self = this
    this.stopBusy()
    this.busyTimer = setTimeout(function() {
      self.element.setAttribute("aria-busy", "true")
      const results = self.element.querySelector("[data-live-filter-region='results']")
      if (results) results.classList.add("is-filtering")
    }, 150)
  }

  stopBusy() {
    if (this.busyTimer) {
      clearTimeout(this.busyTimer)
      this.busyTimer = null
    }
    this.element.removeAttribute("aria-busy")
    const results = this.element.querySelector("[data-live-filter-region='results']")
    if (results) results.classList.remove("is-filtering")
  }

  disableTurbolinksCache() {
    let meta = document.head.querySelector("meta[name='turbolinks-cache-control']")
    if (!meta) {
      meta = document.createElement("meta")
      meta.setAttribute("name", "turbolinks-cache-control")
      document.head.appendChild(meta)
    }
    meta.setAttribute("content", "no-cache")
  }
}
